import React, {
  Dispatch,
  FormEventHandler,
  SetStateAction,
  useCallback,
  useMemo,
} from 'react'
import styled from 'styled-components/macro'
import { ModalFormId } from 'src/components/ModalForms'
import {
  useLabelCategories,
  useLabels,
} from 'src/pages/AccountSettings/AccountSettings.hooks'
import { performMetadataSearch } from 'src/Utils/search/search'
import {
  getSearchQueryString,
  parseSearchQuery,
} from 'src/Utils/search/search.helpers'
import { SearchableData } from 'src/Utils/search/search.types'
import { getPluralization } from 'src/Utils/globalHelpers'
import palette from 'src/designSystem/variables/palette'
import { Spinner } from 'src/Throbber'
import { NONE_VALUE } from '../IndexPage/FilterBar/FilterBar.constants'
import { DropdownItemType } from '../IndexPage/FilterBar/FilterDropdowns/FilterDropdown.constants'
import { TextButton } from '../Buttons/TextButton'
import { FlexAlign, FlexRow } from '../Flex'
import {
  CloseIcon,
  SearchBar,
  SearchIcon,
  SearchInput,
  SearchInputContainer,
} from '../Search/Search.styles'
import { groupLabelsByCategory } from '../Labels/labelGroups'

const PickerHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 4px;
`

// Search fills the rest of the filter row, about an inch clear of the last filter.
const SearchSlot = styled.div`
  flex: 1;
  min-width: 200px;
  margin-left: 96px;

  ${SearchBar} {
    width: 100%;
  }
`

const PickerTitle = styled.p`
  padding-bottom: 10px;
  font-size: 14px;
  color: ${palette.neutral[900]};
`

// The list keeps one height whatever it holds, so the dialog doesn't shrink as filters
// narrow the list (which also kept the filter menus from fitting).
const ListArea = styled.div`
  height: 350px;
  overflow-y: auto;
  width: 100%;
  margin-top: 16px;
`

const ListMessage = styled.div`
  display: flex;
  justify-content: center;
  padding: 32px 0;
  text-align: center;
`

const SelectedCount = styled.span`
  margin-left: auto;
  font-size: 13px;
`

// Labels grouped under their categories, after a "None" option.
export const useLabelFilterOptions = (): {
  options: DropdownItemType[]
  isLoading: boolean
} => {
  const { data: labelsData, isLoading: isLabelsLoading } = useLabels()
  const { data: categoriesData, isLoading: isCategoriesLoading } =
    useLabelCategories()
  const options = useMemo(() => {
    const groups = groupLabelsByCategory(
      labelsData?.getItemsList() ?? [],
      categoriesData?.getItemsList() ?? [],
    )
    return [
      NONE_VALUE,
      ...groups.flatMap((group) =>
        group.name
          ? [
              {
                value: group.name,
                label: group.name,
                nestedFilter: {
                  filterParam: group.name,
                  filterValues: group.labels,
                },
              },
            ]
          : group.labels,
      ),
    ]
  }, [labelsData, categoriesData])
  return { options, isLoading: isLabelsLoading || isCategoriesLoading }
}

// The items a search query matches, as the index pages' search matches them.
export const matchSearch = <T,>(
  items: T[],
  searchable: SearchableData<T>[],
  query: string,
  getId: (item: T) => string,
): T[] => {
  if (!query.trim()) return items
  const { includedIds, excludedIds } = performMetadataSearch(
    searchable,
    parseSearchQuery(getSearchQueryString(query.trim())),
  )
  const included = new Set(includedIds)
  const excluded = new Set(excludedIds)
  return items.filter(
    (item) => included.has(getId(item)) && !excluded.has(getId(item)),
  )
}

// Selection over a filtered list: Select all and Deselect all act on the visible ids
// and keep selections made under other filters.
export const usePickerSelection = (
  selectedIds: string[],
  setSelectedIds: Dispatch<SetStateAction<string[]>>,
  visibleIds: string[],
): {
  selectedSet: Set<string>
  onToggle: (id: string) => void
  selectVisible: () => void
  deselectVisible: () => void
} => {
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const onToggle = useCallback(
    (id: string) =>
      setSelectedIds((previous) =>
        previous.includes(id)
          ? previous.filter((selected) => selected !== id)
          : [...previous, id],
      ),
    [setSelectedIds],
  )
  const selectVisible = () =>
    setSelectedIds((previous) =>
      Array.from(new Set([...previous, ...visibleIds])),
    )
  const deselectVisible = () => {
    const visible = new Set(visibleIds)
    setSelectedIds((previous) => previous.filter((id) => !visible.has(id)))
  }
  return { selectedSet, onToggle, selectVisible, deselectVisible }
}

type PickerFrameProps = {
  formId: ModalFormId
  onSubmit: FormEventHandler<HTMLFormElement>
  // Text shown above the filters.
  title?: string
  // The filter dropdowns, shown before the search box.
  filters: React.ReactNode
  search: string
  setSearch: (search: string) => void
  // What the list holds, singular, e.g. "control".
  noun: string
  selectedCount: number
  visibleCount: number
  totalCount: number
  isFiltered: boolean
  onSelectAll: () => void
  onDeselectAll: () => void
  disabled?: boolean
  hideSelectButtons?: boolean
  // Buttons shown after Select all / Deselect all.
  extraSelectButtons?: JSX.Element
  isLoading: boolean
  emptyState?: JSX.Element
  // The rows, shown when any are visible.
  children: React.ReactNode
}

/**
 * The frame a picker modal's list sits in: filters and a search line, select /
 * deselect all with a count of what is selected, and a list of fixed height.
 */
export const PickerFrame = ({
  formId,
  onSubmit,
  title,
  filters,
  search,
  setSearch,
  noun,
  selectedCount,
  visibleCount,
  totalCount,
  isFiltered,
  onSelectAll,
  onDeselectAll,
  disabled,
  hideSelectButtons,
  extraSelectButtons,
  isLoading,
  emptyState,
  children,
}: PickerFrameProps): JSX.Element => {
  const plural = getPluralization(noun, 2)
  const countText = `${selectedCount} ${getPluralization(
    noun,
    selectedCount,
  )} selected${isFiltered ? ` · showing ${visibleCount} of ${totalCount}` : ''}`

  let list: React.ReactNode
  if (isLoading) {
    list = (
      <ListMessage>
        <Spinner color="primary" size="xl" />
      </ListMessage>
    )
  } else if (visibleCount === 0) {
    list =
      totalCount === 0 && emptyState ? (
        emptyState
      ) : (
        <ListMessage>
          {totalCount === 0
            ? `There are no ${plural} to choose from.`
            : `No ${plural} match these filters.`}
        </ListMessage>
      )
  } else {
    list = children
  }

  return (
    <form id={formId} onSubmit={onSubmit}>
      {!!title && <PickerTitle>{title}</PickerTitle>}
      <PickerHeader>
        <FlexRow
          gap={8}
          align={FlexAlign.CENTER}
          justify={FlexAlign.FLEX_START}
        >
          {filters}
          <SearchSlot>
            <SearchBar>
              <SearchInputContainer>
                <SearchIcon />
                <SearchInput
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  // Enter must not submit the modal's form.
                  onKeyDown={(e) => e.key === 'Enter' && e.preventDefault()}
                  placeholder={`Search ${plural}`}
                  aria-label={`Search ${plural}`}
                />
                {search.length > 0 && (
                  <CloseIcon
                    role="button"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                    // Keep focus in the search box.
                    onMouseDown={(e) => e.preventDefault()}
                  />
                )}
              </SearchInputContainer>
            </SearchBar>
          </SearchSlot>
        </FlexRow>
        <FlexRow
          gap={16}
          align={FlexAlign.CENTER}
          justify={FlexAlign.FLEX_START}
        >
          {!hideSelectButtons && (
            <>
              <TextButton
                onClick={(e) => {
                  e.preventDefault()
                  onSelectAll()
                }}
                disabled={disabled}
              >
                Select all
              </TextButton>
              <TextButton
                onClick={(e) => {
                  e.preventDefault()
                  onDeselectAll()
                }}
                disabled={disabled}
              >
                Deselect all
              </TextButton>
            </>
          )}
          {extraSelectButtons}
          <SelectedCount>{countText}</SelectedCount>
        </FlexRow>
      </PickerHeader>
      <ListArea>{list}</ListArea>
    </form>
  )
}
